import { Component, OnInit, inject } from '@angular/core';

import { AuthenticationService } from '../../core/auth/authentication.service';
import { RedirectService } from '../../core/navigation/redirect.service';
import { RouteParamsService } from '../../core/navigation/route-params.service';

const ZENDESK_LOGIN_PATH = '/zendesk/login';

/**
 * Zendesk SSO logout page (DEV-11861).
 *
 * Sibling of the Zendesk login page. Zendesk redirects the browser here (with
 * the original deep link as `return_to`) to end the Backoffice session before
 * sending the user back through SSO. On load it signs the user out via the
 * shared Rails sign_out endpoint — a GET, so no CSRF token is required, unlike
 * the login page's POST — and then full-page redirects to `/zendesk/login`,
 * forwarding `return_to` so the deep link survives the round trip. The seamless
 * attempt on the login page then cleanly fails (the cookies are gone) and shows
 * the credential form.
 *
 * The redirect goes through the injectable {@link RedirectService} seam and the
 * inbound param is read via {@link RouteParamsService}, keeping the component
 * unit-testable without booting AngularJS / ui-router.
 */
@Component({
  selector: 'app-zendesk-logout',
  standalone: true,
  template: `
    <div class="zendesk-logout">
      <div class="zendesk-logout-form col-sm-5">
        <div class="zendesk-logout-panel panel panel-default panel-inverted">
          <h3 class="zendesk-logout-panel-header">Signing you out&hellip;</h3>
        </div>
      </div>
    </div>
  `,
})
export class ZendeskLogoutComponent implements OnInit {
  private readonly auth = inject(AuthenticationService);
  private readonly redirect = inject(RedirectService);
  private readonly routeParams = inject(RouteParamsService);

  ngOnInit(): void {
    void this.logout();
  }

  /**
   * Best-effort sign-out: end the Backoffice session, then redirect to the
   * Zendesk login page regardless of outcome. `AuthenticationService.logout()`
   * only clears the local cookies (`destroy()`) on a successful round trip, so
   * on failure we force `destroy()` ourselves rather than leave the user in a
   * half-logged-in state. The user asked to leave; logout is final.
   */
  async logout(): Promise<void> {
    try {
      await this.auth.logout();
    } catch {
      this.auth.destroy();
    }
    this.redirect.to(this.buildLoginUrl(this.routeParams.get('return_to')));
  }

  /**
   * Build the `/zendesk/login` URL, forwarding the inbound `return_to` (the
   * deep link Zendesk attached to the logout URL) re-encoded into the query
   * string. Falls back to the bare path when no `return_to` is present — the
   * login page tolerates a missing param.
   */
  private buildLoginUrl(returnTo: string | null): string {
    if (!returnTo) {
      return ZENDESK_LOGIN_PATH;
    }
    return `${ZENDESK_LOGIN_PATH}?return_to=${encodeURIComponent(returnTo)}`;
  }
}
